import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, Loader2, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const THEMES = [
  { v: "classic", l: "Classic" },
  { v: "modern", l: "Modern" },
  { v: "boho", l: "Boho" },
  { v: "luxury", l: "Luxury" },
  { v: "heritage", l: "Heritage" },
];

export default function OccasionAnalyze() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [occasions, setOccasions] = useState([]);
  const [occasion, setOccasion] = useState(params.get("occasion") || "");
  const [theme, setTheme] = useState("classic");

  const selfieRef = useRef();
  const bodyRef = useRef();
  const [selfie, setSelfie] = useState(null);
  const [body, setBody] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [bodyPreview, setBodyPreview] = useState(null);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/occasions").then((r) => {
      setOccasions(r.data.occasions || []);
      if (!occasion && r.data.occasions?.length) setOccasion(r.data.occasions[0].id);
    });
  }, [occasion]);

  const pickFile = (f, setF, setP) => {
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Use JPEG, PNG or WEBP.");
      return;
    }
    setF(f);
    setP(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!selfie) { toast.error("Selfie required."); return; }
    if (!occasion) { toast.error("Pick an occasion."); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", selfie);
    if (body) fd.append("full_body", body);
    try {
      const res = await api.post(
        `/occasion/analyze?occasion=${encodeURIComponent(occasion)}&theme=${encodeURIComponent(theme)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Your ceremony reading is ready.");
      await refresh();
      navigate(`/occasion/${res.data.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Analysis failed. Please try again.";
      toast.error(msg);
      if (e?.response?.status === 402) navigate("/pricing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="crown-container py-12 md:py-16" data-testid="occasion-analyze-page">
      <p className="font-overline mb-3">A new reading</p>
      <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-2">Compose the ceremony.</h1>
      <p className="text-ash max-w-2xl mb-12">
        Two photos at most — a clear selfie, and (optional but recommended) a full-body
        shot so we can read your silhouette and propose a precise dress cut.
      </p>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* LEFT — uploads */}
        <section className="lg:col-span-7 space-y-6">
          {/* Selfie */}
          <div>
            <p className="font-overline text-ash mb-3">Selfie · required</p>
            <label htmlFor="occ-selfie" className="block aspect-[4/5] relative overflow-hidden border border-dashed border-bone/20 cursor-pointer hover:border-gold transition-colors" data-testid="occ-selfie-dropzone">
              {selfiePreview ? (
                <img src={selfiePreview} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-obsidian/30">
                  <Upload className="text-gold" strokeWidth={1} size={36} />
                  <p className="font-overline text-bone">Drop selfie</p>
                </div>
              )}
              <input
                id="occ-selfie"
                data-testid="occ-selfie-input"
                ref={selfieRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0], setSelfie, setSelfiePreview)}
              />
            </label>
          </div>
          {/* Full body */}
          <div>
            <p className="font-overline text-ash mb-3">Full-body · optional · better silhouette read</p>
            <label htmlFor="occ-body" className="block aspect-[3/5] sm:aspect-[16/10] relative overflow-hidden border border-dashed border-bone/20 cursor-pointer hover:border-gold transition-colors" data-testid="occ-body-dropzone">
              {bodyPreview ? (
                <img src={bodyPreview} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-obsidian/30">
                  <Upload className="text-gold" strokeWidth={1} size={32} />
                  <p className="font-overline text-bone">Drop full-body</p>
                </div>
              )}
              <input
                id="occ-body"
                data-testid="occ-body-input"
                ref={bodyRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0], setBody, setBodyPreview)}
              />
            </label>
          </div>
        </section>

        {/* RIGHT — picker */}
        <section className="lg:col-span-5 lg:pt-2">
          <p className="font-overline text-ash mb-4">The occasion</p>
          <div className="grid grid-cols-2 gap-2 mb-10" data-testid="occasion-select">
            {occasions.map((o) => (
              <button
                key={o.id}
                onClick={() => setOccasion(o.id)}
                data-testid={`pick-occasion-${o.id}`}
                className={`text-left p-4 border transition-colors ${
                  occasion === o.id
                    ? "border-gold bg-gold/5"
                    : "border-bone/15 hover:border-bone/40"
                }`}
              >
                <p className="font-serif text-base leading-tight">{o.name}</p>
                <p className="font-overline text-[0.6rem] text-ash mt-2">{o.subtitle}</p>
              </button>
            ))}
          </div>

          <p className="font-overline text-ash mb-4">Vibe</p>
          <div className="grid grid-cols-3 gap-2 mb-10">
            {THEMES.map((t) => (
              <button
                key={t.v}
                data-testid={`theme-${t.v}`}
                onClick={() => setTheme(t.v)}
                className={`px-4 py-3 font-overline text-xs border transition-colors ${
                  theme === t.v ? "border-gold text-gold bg-gold/5" : "border-bone/15 text-ash hover:text-bone"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>

          <button
            onClick={submit}
            disabled={loading || !selfie || !occasion}
            className="crown-btn-primary w-full justify-center"
            data-testid="occ-submit-btn"
          >
            {loading ? (
              <><Loader2 className="animate-spin" size={16} strokeWidth={1.5} /> Composing your look…</>
            ) : (
              <>Reveal the look <ArrowUpRight size={16} strokeWidth={1.5} /></>
            )}
          </button>
          {!user?.is_premium && (
            <p className="font-overline text-ash mt-6">1 free ceremony reading · upgrade for unlimited + try-on</p>
          )}
        </section>
      </div>
    </main>
  );
}
